import { isNewToastMessage, type ToastMessage } from '@app-builder/models/toast-session';
import { getClientEnv } from '@app-builder/utils/environment';
import { useEffect } from 'react';
import { ToastBar, Toaster, toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Icon } from 'ui-icons';

export function MarbleToaster({ toastMessage }: { toastMessage?: ToastMessage }) {
  const { t } = useTranslation(['common']);

  useEffect(() => {
    if (!toastMessage) return;

    if (isNewToastMessage(toastMessage)) {
      const { type, message } = toastMessage;
      toast[type](getMessage(message));
    } else {
      const { type, messageKey } = toastMessage;
      const message = t(messageKey);

      toast[type](getMessage(message));
    }
  }, [t, toastMessage]);

  return (
    <Toaster
      position="top-center"
      toastOptions={{
        className: 'bg-surface-card text-grey-primary',
        style: {
          background: 'var(--color-surface-card)',
          color: 'var(--color-grey-primary)',
        },
        loading: {
          icon: LoaderIcon,
        },
        success: {
          icon: SuccessIcon,
        },
        error: {
          icon: ErrorIcon,
        },
      }}
    >
      {(currentToast) => (
        <ToastBar
          toast={currentToast}
          style={{
            background: 'var(--color-surface-card)',
            color: 'var(--color-grey-primary)',
          }}
        >
          {({ icon, message }) => (
            <>
              {icon}
              {message}
              {currentToast.type !== 'loading' ? (
                <button onClick={() => toast.dismiss(currentToast.id)} aria-label="Close" className="shrink-0">
                  <Icon icon="cross" className="size-6 shrink-0" />
                </button>
              ) : null}
            </>
          )}
        </ToastBar>
      )}
    </Toaster>
  );
}

function getMessage(message: string) {
  if (getClientEnv('ENV') !== 'development') {
    return message;
  }

  return (
    <div className="flex flex-col gap-xs">
      <p className="text-s text-grey-primary">{message}</p>
      <p className="text-grey-secondary text-xs">In dev, toast may be displayed twice due to strict mode</p>
    </div>
  );
}

const LoaderIcon = (
  <div
    aria-hidden
    className="border-grey-border border-r-grey-placeholder box-border size-4 shrink-0 animate-spin rounded-full border-2 border-solid"
  />
);

const ErrorIcon = (
  <div
    aria-hidden
    className="animate-circle-animation after:animate-first-line-animation after:bg-[#FFFFFF] before:animate-second-line-animation before:bg-[#FFFFFF] bg-red-primary relative size-5 shrink-0 rotate-45 rounded-full delay-100 before:absolute before:bottom-[9px] before:start-xs before:h-[2px] before:w-3 before:rounded-lg before:delay-150 after:absolute after:bottom-[9px] after:start-xs after:h-[2px] after:w-3 after:rounded-lg after:delay-150"
  />
);

const SuccessIcon = (
  <div
    aria-hidden
    className="animate-circle-animation after:animate-checkmark-animation after:border-grey-white bg-green-primary relative size-5 shrink-0 rotate-45 rounded-full delay-100 after:absolute after:bottom-[6px] after:start-[6px] after:box-border after:h-[10px] after:w-[6px] after:border-b-2 after:border-r-2 after:border-solid after:delay-200"
  />
);
